import React, { useState, useCallback, useMemo } from "react";
import {
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
  Platform,
} from "react-native";
import color from "@constants/color";
import Text from "@components/CustomText";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import toast from "@utils/toast";
import DateTimePicker from "@react-native-community/datetimepicker";
import { useOnboarding } from "./OnboardingContext";
import { useFlowNavigation } from "./useFlowNavigation";
import { DEFAULT_FLOW_CONFIG } from "./FlowConfigContext";
import { fontFamily } from "@utils/styles/fonts";
import type { StackNavigationProp } from "@react-navigation/stack";
import type { OnboardingStackParamList } from "./types";
import type { FieldConfig, FieldId } from "./flowConfig.types";

interface PersonalDetailsProps {
  navigation: StackNavigationProp<OnboardingStackParamList, "PersonalDetails">;
}

type FormState = Partial<Record<FieldId, string>>;

const PersonalDetails: React.FC<PersonalDetailsProps> = ({ navigation }) => {
  const { data, updateData } = useOnboarding();
  const { goToNextStep, getStepConfig } = useFlowNavigation();

  // Prefer fields from active flow_config; fall back to the bundled default
  const stepCfg = getStepConfig<"PersonalDetails">();
  const fields = useMemo<FieldConfig[]>(
    () =>
      stepCfg?.fields ??
      (
        DEFAULT_FLOW_CONFIG.db_types[0].steps.find(
          (s) => s.id === "PersonalDetails",
        )?.config as { fields: FieldConfig[] } | undefined
      )?.fields ??
      [],
    [stepCfg],
  );

  const [form, setForm] = useState<FormState>({
    aadhaarNumber: data.aadhaarNumber,
  });

  // The Aadhaar number was entered and checked (uniqueness/fraud) on the
  // AadhaarVerification step — here it is display-only: editing it after the
  // check would submit an unchecked number. Locked only when actually
  // prefilled, so flow configs without the verification step keep working.
  const aadhaarLocked = !!data.aadhaarNumber;

  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isFormValid = useMemo(() => {
    return fields
      .filter((f) => f.required)
      .every((f) => (form[f.id] ?? "").trim().length > 0);
  }, [fields, form]);

  const updateField = useCallback((id: FieldId, value: string) => {
    setForm((prev) => ({ ...prev, [id]: value }));
  }, []);

  // ─── Field-type handlers ───────────────────────────────────────────────────

  const makeNumericHandler = useCallback(
    (id: FieldId, maxLen: number) => (text: string) => {
      updateField(id, text.replace(/[^\d]/g, "").slice(0, maxLen));
    },
    [updateField],
  );

  const makeAlphanumericHandler = useCallback(
    (id: FieldId, maxLen: number, upper = false) =>
      (text: string) => {
        const cleaned = text.replace(/[^a-zA-Z0-9]/g, "").slice(0, maxLen);
        updateField(id, upper ? cleaned.toUpperCase() : cleaned);
      },
    [updateField],
  );

  const makePhoneHandler = useCallback(
    (id: FieldId) => (text: string) => {
      updateField(id, text.replace(/[^\d]/g, "").slice(0, 10));
    },
    [updateField],
  );

  const formatDate = useCallback((date: Date): string => {
    const dd = date.getDate().toString().padStart(2, "0");
    const mm = (date.getMonth() + 1).toString().padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${dd} / ${mm} / ${yyyy}`;
  }, []);

  const toIsoDob = useCallback((dob: string): string => {
    const parts = dob.split("/").map((s) => s.trim());
    if (parts.length !== 3) return "";
    const [dd, mm, yyyy] = parts;
    if (!dd || !mm || !yyyy) return "";
    return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
  }, []);

  const onDateChange = useCallback(
    (_: unknown, selectedDate?: Date) => {
      setShowDatePicker(Platform.OS === "ios");
      if (selectedDate) {
        updateField("dateOfBirth", formatDate(selectedDate));
      }
    },
    [updateField, formatDate],
  );

  const onContinue = useCallback(async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      updateData({
        fullName: form.fullName ?? "",
        dateOfBirth: toIsoDob(form.dateOfBirth ?? ""),
        city: form.city ?? "",
        aadhaarNumber: form.aadhaarNumber ?? "",
        panNumber: form.panCardNumber ?? "",
        drivingLicenseNumber: form.drivingLicenseNumber ?? "",
        homePhoneNumber: form.homePhoneNumber ?? "",
        refereeName: form.refereeName ?? "",
        refereeNumber: form.refereeNumber ?? "",
        refereePhone1: form.refereeNumber ?? "",
      });
      goToNextStep();
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } } };
      toast.showLongToast(
        axiosError?.response?.data?.message ||
          "Something went wrong. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, form, updateData, goToNextStep, toIsoDob]);

  // ─── Per-field renderer ────────────────────────────────────────────────────

  const renderField = useCallback(
    (fieldCfg: FieldConfig) => {
      const { id, label, placeholder, type, validation } = fieldCfg;
      const isFocused = focusedField === id;
      const raw = form[id] ?? "";

      if (type === "date") {
        return (
          <View key={id} style={styles.fieldGroup}>
            <Text style={styles.fieldLabel}>{label}</Text>
            <Pressable
              style={[styles.inputBox, isFocused && styles.inputBoxFocused]}
              onPress={() => setShowDatePicker(true)}
              accessibilityRole="button"
              accessibilityLabel={label}
            >
              <Text style={raw ? styles.inputText : styles.inputPlaceholder}>
                {raw || "Select Date"}
              </Text>
              <Text style={styles.calendarIcon}>🗓</Text>
            </Pressable>
          </View>
        );
      }

      // Determine display value and change handler based on field type / id
      let displayValue = raw;
      let onChangeText: (text: string) => void;
      let keyboardType: TextInput["props"]["keyboardType"] = "default";
      let autoCapitalize: TextInput["props"]["autoCapitalize"] = "words";
      const maxLen = validation?.exact_length ?? validation?.max_length;

      if (type === "numeric") {
        // Aadhaar: format display with spaces, strip for storage
        if (id === "aadhaarNumber") {
          displayValue = raw.replace(/(\d{4})(?=\d)/g, "$1 ");
        }
        onChangeText = makeNumericHandler(id, maxLen ?? 20);
        keyboardType = "number-pad";
        autoCapitalize = "none";
      } else if (type === "phone") {
        onChangeText = makePhoneHandler(id);
        keyboardType = "number-pad";
        autoCapitalize = "none";
      } else if (type === "alphanumeric") {
        const upper = id === "panCardNumber";
        onChangeText = makeAlphanumericHandler(id, maxLen ?? 30, upper);
        autoCapitalize = upper ? "characters" : "none";
      } else {
        onChangeText = (text) => updateField(id, text);
      }

      const locked = id === "aadhaarNumber" && aadhaarLocked;

      return (
        <View key={id} style={styles.fieldGroup}>
          <Text style={styles.fieldLabel}>{label}</Text>
          <View
            style={[
              styles.inputBox,
              isFocused && styles.inputBoxFocused,
              locked && styles.inputBoxDisabled,
            ]}
          >
            <TextInput
              value={displayValue}
              editable={!locked}
              style={[styles.inputText, locked && styles.inputTextDisabled]}
              placeholder={placeholder}
              placeholderTextColor={PLACEHOLDER_COLOR}
              keyboardType={keyboardType}
              maxLength={
                id === "aadhaarNumber" && maxLen
                  ? maxLen + Math.floor(maxLen / 4) - 1 // account for spaces
                  : maxLen
              }
              onChangeText={onChangeText}
              onFocus={() => setFocusedField(id)}
              onBlur={() => setFocusedField(null)}
              autoCapitalize={autoCapitalize}
              accessibilityLabel={id}
            />
          </View>
        </View>
      );
    },
    [
      focusedField,
      form,
      aadhaarLocked,
      makeNumericHandler,
      makePhoneHandler,
      makeAlphanumericHandler,
      updateField,
    ],
  );

  return (
    <>
      <HeaderWithBackAndTitle
        headertitle="Personal Details"
        navigation={navigation}
        hideShadow
      />

      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        {fields.map(renderField)}
      </ScrollView>

      <View style={styles.bottomBar}>
        <Pressable
          style={[
            styles.continueButton,
            !isFormValid && styles.continueButtonDisabled,
          ]}
          disabled={isSubmitting || !isFormValid}
          onPress={onContinue}
          accessibilityRole="button"
          accessibilityLabel="Continue"
        >
          <Text style={styles.continueButtonText}>Continue</Text>
        </Pressable>
      </View>

      {showDatePicker && (
        <DateTimePicker
          value={new Date(2000, 0, 1)}
          mode="date"
          display="default"
          maximumDate={new Date()}
          onChange={onDateChange}
        />
      )}
    </>
  );
};

const BORDER_COLOR = color.color_D9D9D9;
const TEXT_DARK = color.Gray20;
const PLACEHOLDER_COLOR = color.disableFont;
const DISABLED_BTN = color.lighterBlack;

const styles = StyleSheet.create({
  scrollContent: {
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 108,
  },
  fieldGroup: {
    marginBottom: 16,
  },
  fieldLabel: {
    fontSize: 14,
    fontFamily: fontFamily.semibold ?? fontFamily.medium,
    color: TEXT_DARK,
    marginBottom: 6,
  },
  inputBox: {
    height: 52,
    borderWidth: 1,
    borderColor: BORDER_COLOR,
    borderRadius: 8,
    paddingHorizontal: 16,
    backgroundColor: color.white,
    flexDirection: "row",
    alignItems: "center",
  },
  inputBoxFocused: {
    borderColor: color.primary,
  },
  // Verified-on-a-previous-step fields (Aadhaar) render read-only.
  inputBoxDisabled: {
    backgroundColor: "#F5F5F5",
  },
  inputTextDisabled: {
    color: "#8A8A8A",
  },
  inputText: {
    flex: 1,
    fontSize: 14,
    fontFamily: fontFamily.regular,
    color: TEXT_DARK,
    padding: 0,
    margin: 0,
  },
  inputPlaceholder: {
    flex: 1,
    fontSize: 14,
    fontFamily: fontFamily.regular,
    color: PLACEHOLDER_COLOR,
  },
  calendarIcon: {
    fontSize: 20,
    color: color.color_808080,
    marginLeft: 8,
  },
  bottomBar: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: color.white,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: color.lineColor,
  },
  continueButton: {
    backgroundColor: color.primary,
    borderRadius: 12,
    paddingVertical: 15,
    alignItems: "center",
  },
  continueButtonDisabled: {
    backgroundColor: DISABLED_BTN,
  },
  continueButtonText: {
    fontSize: 16,
    fontFamily: fontFamily.semibold ?? fontFamily.medium,
    color: color.white,
  },
});

export default PersonalDetails;
